/**
 * Light / dark resolution for the Haseeb website.
 *
 * The mode is decided by the visitor's local clock — 06:00-17:59 light,
 * 18:00-05:59 dark — NOT by the OS. `prefers-color-scheme` is a no-JS fallback
 * handled in CSS; once this module runs, the clock (or an explicit override) wins.
 *
 * Precedence, highest first:
 *   1. `?theme=light|dark` in the URL — for screenshots and QA.
 *   2. `localStorage.haseeb.theme` set to `light` or `dark` by the header control.
 *   3. The local clock.
 *
 * The first paint is handled by the blocking script in index.html. This module
 * keeps the rest of the session in sync: it re-evaluates on the hour and whenever
 * the tab regains focus, so a session left open across 18:00 flips without a reload.
 */

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = ThemeMode | 'auto';

const STORAGE_KEY = 'haseeb.theme';

/** Canvas token per mode. Mirrors --canvas in index.css. */
const CANVAS: Record<ThemeMode, string> = {
  light: '#E6E6E6',
  dark: '#141414',
};

const LIGHT_FROM_HOUR = 6;
const LIGHT_UNTIL_HOUR = 18;

const listeners = new Set<(mode: ThemeMode) => void>();
let hourlyTimer: ReturnType<typeof setTimeout> | undefined;

function isMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

/** The mode the clock alone would pick. */
export function clockMode(now: Date = new Date()): ThemeMode {
  const hour = now.getHours();
  return hour >= LIGHT_FROM_HOUR && hour < LIGHT_UNTIL_HOUR ? 'light' : 'dark';
}

/** The `?theme=` override, if the URL carries a valid one. */
function urlOverride(): ThemeMode | null {
  try {
    const value = new URLSearchParams(window.location.search).get('theme');
    return isMode(value) ? value : null;
  } catch {
    return null;
  }
}

/** The visitor's stored choice. `auto` (the default) hands back to the clock. */
export function getPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isMode(stored) ? stored : 'auto';
  } catch {
    return 'auto';
  }
}

/** Resolve the mode that should be showing right now. */
export function resolveMode(): ThemeMode {
  const forced = urlOverride();
  if (forced) return forced;

  const preference = getPreference();
  if (isMode(preference)) return preference;

  return clockMode();
}

/** The mode currently painted, read back off the document. */
export function currentMode(): ThemeMode {
  const attr = document.documentElement.getAttribute('data-theme');
  return isMode(attr) ? attr : resolveMode();
}

/** Paint a mode: swap the token set, the FOUC background and the browser chrome. */
export function applyMode(mode: ThemeMode): void {
  const root = document.documentElement;
  if (root.getAttribute('data-theme') === mode) return;

  root.setAttribute('data-theme', mode);
  root.style.backgroundColor = CANVAS[mode];

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', CANVAS[mode]);

  listeners.forEach((listener) => listener(mode));
}

/** Re-resolve and paint. Safe to call as often as you like. */
export function syncMode(): ThemeMode {
  const mode = resolveMode();
  applyMode(mode);
  return mode;
}

/**
 * Record a manual choice. `auto` clears the override and returns to the clock.
 * An explicit light/dark sticks until the visitor picks `auto` again.
 */
export function setPreference(preference: ThemePreference): ThemeMode {
  try {
    if (preference === 'auto') {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, preference);
    }
  } catch {
    /* storage blocked (private mode) — the mode still applies for this session */
  }
  return syncMode();
}

export function subscribe(listener: (mode: ThemeMode) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Milliseconds until the next wall-clock hour, plus a one second cushion. */
function msUntilNextHour(now: Date = new Date()): number {
  return (60 - now.getMinutes()) * 60_000 - now.getSeconds() * 1000 + 1_000;
}

function scheduleHourlyCheck(): void {
  if (hourlyTimer) clearTimeout(hourlyTimer);
  hourlyTimer = setTimeout(() => {
    syncMode();
    scheduleHourlyCheck();
  }, msUntilNextHour());
}

/**
 * Start keeping the mode honest for the life of the session.
 * Returns a teardown function.
 */
export function startThemeWatcher(): () => void {
  syncMode();
  scheduleHourlyCheck();

  const onFocus = () => syncMode();
  const onVisibility = () => {
    if (document.visibilityState === 'visible') syncMode();
  };

  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    if (hourlyTimer) clearTimeout(hourlyTimer);
    hourlyTimer = undefined;
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
